/**
 * Required Fields Validation Logic
 *
 * Pure validation function that checks whether all required fields
 * have valid values across all benefit plans in a coverage.
 *
 * Handles:
 * - Benefit-level fields (in plan.benefits, transformed via LOC transforms)
 * - Plan-level fields (on BaseBenefitPlan directly, e.g., currentCarrierName)
 * - editableWhen conditionals (fields disabled by parent field values are skipped)
 * - Network-dependent fields are currently skipped (TODO: future enhancement)
 */

import isEqual from "lodash/isEqual";

import { BaseBenefitPlanPolicyPeriod } from "../../types/rfp.api.types";

import { EMPTY_VALIDATION_RESULT } from "./types";

import type { BenefitDefinitions } from "../../benefits/benefit.types";
import type { RfpBenefitPlan } from "../../types/rfp.api.types";
import type { MissingField, RequiredFieldsValidationResult } from "./types";

/**
 * Derives the set of required field keys from a LOC's BenefitDefinitions.
 *
 * Iterates all definitions and collects keys where `isRequired` is `true`.
 * This is the single source of truth: required-ness is declared on each
 * definition rather than in a separate config map.
 *
 * @param definitions - The LOC's BenefitDefinitions object
 * @returns ReadonlySet of field keys that are required
 */
export function getRequiredFieldKeys(
  definitions: BenefitDefinitions,
): ReadonlySet<string> {
  const keys = new Set<string>();
  for (const [key, def] of Object.entries(definitions)) {
    if (def.isRequired) {
      keys.add(key);
    }
  }
  return keys;
}

export const hasCurrentPlans = (benefitPlans: RfpBenefitPlan[]): boolean =>
  benefitPlans.some(
    (plan) => plan.policyPeriod === BaseBenefitPlanPolicyPeriod.current,
  );

/**
 * Checks whether a value counts as "present" (not missing).
 *
 * A value is considered missing if it is:
 * - null or undefined
 * - empty string
 * - an empty array (used for required multi-select/list fields)
 *
 * If `invalidValues` is provided, any value that deeply equals one of
 * the invalid entries is treated as missing regardless of the rules
 * above (e.g. a 1 can be explicitly invalidated).
 *
 * If `acceptableValues` is provided, any value that deeply equals one of
 * the acceptable entries is treated as present regardless of the rules
 * above (e.g. an empty array or `null` can be explicitly accepted).
 *
 * If a value is present in both `acceptableValues` and `invalidValues`,
 * `acceptableValues` takes precedence and the value is treated as present.
 *
 */
export function isValuePresent(
  value: unknown,
  invalidValues?: unknown[],
  acceptableValues?: unknown[],
): boolean {
  if (acceptableValues && acceptableValues.some((v) => isEqual(v, value))) {
    return true;
  }
  if (invalidValues && invalidValues.some((v) => isEqual(v, value)))
    return false;
  if (value == null) return false;
  if (value === "") return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
}

/**
 * Resolves a benefit-level field value by checking both raw benefits
 * (grid-format keys written by cell edits) and transformed data
 * (from transformFromApi). Cell-edit values take priority because
 * transformFromApi reads from nested API-format keys that are not
 * updated when the user edits a cell.
 */
function resolveBenefitValue(
  fieldKey: string,
  plan: RfpBenefitPlan,
  gridData: Record<string, unknown>,
): unknown {
  const rawBenefits = (plan.benefits ?? {}) as Record<string, unknown>;
  // Prefer the raw grid-format key if explicitly set (written by cell edits)
  if (rawBenefits[fieldKey] !== undefined) {
    return rawBenefits[fieldKey];
  }
  // Fall back to the transformed value (initial API data)
  return gridData[fieldKey];
}

/**
 * Checks whether a single required field is filled or missing for a plan.
 *
 * Returns the MissingField if the field is not present (and is applicable
 * given editableWhen conditionals), or `undefined` if the field is filled
 * or should be skipped.
 */
function checkFieldForPlan(
  fieldKey: string,
  definition: BenefitDefinitions[string],
  plan: RfpBenefitPlan,
  gridData: Record<string, unknown>,
): { missing?: MissingField; counted: boolean } {
  // Skip network-dependent fields (TODO: future enhancement)
  if (definition.networkDependent) {
    return { counted: false };
  }

  // Check editableWhen — if the field is disabled by its parent, skip it.
  // The parent value is resolved from benefits first, then the plan object
  // as fallback. This handles cases where the dependency (e.g. policyPeriod)
  // lives on the plan object, regardless of whether the child field itself
  // is benefitPlanLevel.
  if (definition.editableWhen) {
    const { dependsOn, disabledValues } = definition.editableWhen;
    const parentValue =
      resolveBenefitValue(dependsOn, plan, gridData) ??
      (plan as unknown as Record<string, unknown>)[dependsOn];

    if (parentValue != null && disabledValues.includes(String(parentValue))) {
      return { counted: false };
    }
  }

  // Get the field value from the appropriate source
  const value: unknown = definition.benefitPlanLevel
    ? (plan as unknown as Record<string, unknown>)[fieldKey]
    : resolveBenefitValue(fieldKey, plan, gridData);

  // A field is considered filled if it has a persisted value OR a default.
  // Default values are displayed in the grid and used on save, so they count as "present"
  // for required-field validation. Definition-level defaults take precedence over cell-spec defaults.
  const effectiveValue = isValuePresent(
    value,
    definition.invalidValues,
    definition.acceptableValues,
  )
    ? value
    : (definition.defaultValue ?? definition.cell?.defaultValue);

  if (
    isValuePresent(
      effectiveValue,
      definition.invalidValues,
      definition.acceptableValues,
    )
  ) {
    return { counted: true };
  }

  return {
    counted: true,
    missing: {
      fieldKey,
      label: definition.label,
      planIdentifier: plan.identifier,
      planName: plan.name,
    },
  };
}

/**
 * Validates required fields for a coverage across all its benefit plans.
 *
 * For each benefit plan, transforms benefits from API format to grid format
 * using the provided transform function, then checks each required field key
 * for a valid value.
 *
 * @param benefitPlans - The benefit plans to validate
 * @param requiredFieldKeys - Set of grid-format field keys that must have values
 * @param definitions - The LOC's benefit definitions (for labels and editableWhen config)
 * @param transformFromApi - Function to convert API benefits to flat grid format
 * @returns Validation result with missing fields list
 */
export function validateRequiredFields(
  benefitPlans: RfpBenefitPlan[],
  requiredFieldKeys: ReadonlySet<string>,
  definitions: BenefitDefinitions,
  transformFromApi: (benefits: unknown) => Record<string, unknown>,
): RequiredFieldsValidationResult {
  if (!benefitPlans?.length || requiredFieldKeys.size === 0) {
    return EMPTY_VALIDATION_RESULT;
  }

  const coverageHasCurrentPlans = hasCurrentPlans(benefitPlans);
  const missingFields: MissingField[] = [];
  let totalRequired = 0;
  let totalFilled = 0;

  for (const plan of benefitPlans) {
    const gridData: Record<string, unknown> = plan.benefits
      ? transformFromApi(plan.benefits)
      : {};

    for (const fieldKey of requiredFieldKeys) {
      const definition = definitions[fieldKey];
      if (!definition) continue;
      if (definition.requiredOnCurrentCoverageOnly && !coverageHasCurrentPlans)
        continue;

      const result = checkFieldForPlan(fieldKey, definition, plan, gridData);
      if (!result.counted) continue;

      totalRequired++;
      if (result.missing) {
        missingFields.push(result.missing);
      } else {
        totalFilled++;
      }
    }
  }

  return {
    isValid: missingFields.length === 0,
    missingFields,
    totalRequired,
    totalFilled,
  };
}
